import React, { useEffect, useRef, useState } from 'react'
import { Prompt, useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import AdapterDateFns from '@mui/lab/AdapterDateFns'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import DesktopDatePicker from '@mui/lab/DesktopDatePicker'
import koLocale from 'date-fns/locale/ko'
import Add from '@mui/icons-material/Add'
import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import BottomButtonGroup, { FORM_WIDE_UPLOAD_BUTTON_SX } from '../../component/BottomButtonGroup'
import { FORM_IMAGE_PREVIEW_FRAME_SX, FORM_PANEL_SX, getFormChoiceCardSx } from '../../component/formSurfaceStyles'
import { getLocationState } from '../../utils/listPageState'
import {
    applyBannerDraft,
    applyBannerFieldChange,
    BANNER_IMAGE_FIELD_CONFIGS,
    BANNER_IMAGE_FILE_ACCEPT,
    BANNER_IMAGE_FILE_ERROR_MESSAGE,
    clearBannerDraft,
    createBannerDraftPayload,
    createDefaultBannerData,
    handleBannerBackNavigation,
    handleBannerImageUpload,
    moveToBannerList,
    NEW_BANNER_DRAFT_KEY,
    prepareBannerSubmit,
    readBannerDraft,
    saveBannerDraft,
} from './bannerDraftUtils'
import {
    BANNER_IMAGE_LEAVE_MESSAGE,
    hasPendingBannerImages,
    usePendingBannerImageUnloadGuard,
} from './bannerLeaveGuard'

const PANEL_SX = FORM_PANEL_SX

const IMAGE_PREVIEW_FRAME_SX = {
    ...FORM_IMAGE_PREVIEW_FRAME_SX,
    width: '100%',
    height: { xs: 220, md: 280 },
    mb: 1.5,
}

const IMAGE_PREVIEW_SX = {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    display: 'block',
}

const getChoiceCardSx = (selected, disabled = false) => ({
    ...getFormChoiceCardSx(selected, disabled),
    p: 1.5,
    width: '100%',
})

const BANNER_STATUS_OPTIONS = [
    {
        value: 0,
        title: '미노출',
        description: '관리자에서만 보관하고 사용자 화면에는 표시하지 않습니다.',
    },
    {
        value: 1,
        title: '노출',
        description: '설정한 기간과 위치 기준으로 사용자 화면에 표시합니다.',
    },
]

const getImageFileName = (imageFile) => {
    if (!imageFile || typeof imageFile !== 'object') {
        return ''
    }

    return imageFile.name || ''
}

function FormSection({ title, description, children }) {
    return (
        <Paper variant="outlined" sx={PANEL_SX}>
            <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {description}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {children}
            </Box>
        </Paper>
    )
}

function ChoiceCardLabel({ title, description }) {
    return (
        <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                {title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                {description}
            </Typography>
        </Box>
    )
}

// 카드형 선택 UI에서도 기존 폼 변경 핸들러가 받던 name/value 이벤트 계약을 유지한다.
const createFieldChangeEvent = (name, value) => ({
    target: {
        name,
        value,
    },
})

function SettingChoiceGroup({ title, name, value, options, disabled, onChange }) {
    return (
        <FormControl fullWidth>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {title}
            </Typography>
            <Box
                role="radiogroup"
                aria-label={title}
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                    gap: 1,
                    mt: 1,
                }}
            >
                {options.map((option) => {
                    const checked = String(value) === String(option.value)

                    return (
                        <FormControlLabel
                            key={option.value}
                            value={option.value}
                            control={(
                                <Radio
                                    name={name}
                                    checked={checked}
                                    onChange={() => onChange(createFieldChangeEvent(name, option.value))}
                                    disabled={disabled}
                                />
                            )}
                            disabled={disabled}
                            sx={getChoiceCardSx(checked, disabled)}
                            label={(
                                <ChoiceCardLabel
                                    title={option.title}
                                    description={option.description}
                                />
                            )}
                        />
                    )
                })}
            </Box>
        </FormControl>
    )
}

function BannerImageUploadPanel({
    fieldConfig,
    imageSrc,
    imageFile,
    disabled,
    onUploadClick,
}) {
    const imageFileName = getImageFileName(imageFile)

    return (
        <Paper variant="outlined" sx={PANEL_SX}>
            <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {fieldConfig.previewAlt}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    배너 위치에 맞는 이미지를 등록합니다.
                </Typography>
            </Box>
            <Box sx={IMAGE_PREVIEW_FRAME_SX}>
                <Box
                    component="img"
                    src={imageSrc || '/images/noimage.jpg'}
                    alt={`${fieldConfig.previewAlt} 미리보기`}
                    sx={IMAGE_PREVIEW_SX}
                />
            </Box>
            <FormControl fullWidth>
                <TextField
                    style={{ display: 'none' }}
                    inputProps={{ accept: BANNER_IMAGE_FILE_ACCEPT }}
                    id={fieldConfig.inputId}
                    name={fieldConfig.fieldName}
                    type="file"
                    onChange={event => onUploadClick(event, fieldConfig.imageType)}
                    disabled={disabled}
                />
                <Box component="label" htmlFor={fieldConfig.inputId} sx={{ display: 'block' }}>
                    <Button
                        fullWidth
                        variant="contained"
                        color="success"
                        component="span"
                        startIcon={<Add />}
                        sx={FORM_WIDE_UPLOAD_BUTTON_SX}
                        disabled={disabled}
                    >
                        {fieldConfig.uploadLabel}
                    </Button>
                </Box>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {imageFileName ? `선택한 파일: ${imageFileName}` : '저장 전 미리보기로 이미지가 올바른지 확인하세요.'}
            </Typography>
        </Paper>
    )
}

export default function NewBanner() {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const listCurPage = locationState.curPage || 1
    const listSearchText = locationState.searchText || ''
    const listSearchSelect = locationState.searchSelect || {
        searchLocation: '',
        searchStatus: '',
    }
    const listSearch = location.search || ''
    const draftKey = NEW_BANNER_DRAFT_KEY
    const [data, setData] = useState(() => createDefaultBannerData())
    const [saving, setSaving] = useState(false)
    const [mainImage, setMainImage] = useState('/images/noimage.jpg')
    const [mainImage2, setMainImage2] = useState('/images/noimage.jpg')
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [draftReady, setDraftReady] = useState(false)
    const [navigationUnlocked, setNavigationUnlocked] = useState(false)
    const initialDraftStateRef = useRef(null)
    const initialDraftSnapshotRef = useRef('')
    const leaveGuardEnabled = hasPendingBannerImages(data) && !saving && !navigationUnlocked
    const imageFieldConfigs = BANNER_IMAGE_FIELD_CONFIGS.filter((fieldConfig) => !fieldConfig.topOnly || data.location === 'top')

    usePendingBannerImageUnloadGuard(leaveGuardEnabled)

    if (!initialDraftStateRef.current) {
        initialDraftStateRef.current = {
            data,
        }
        initialDraftSnapshotRef.current = JSON.stringify(createBannerDraftPayload({
            data,
        }))
    }

    useEffect(() => {
        const storedDraft = readBannerDraft(draftKey)
        const initialDraftState = initialDraftStateRef.current

        if (storedDraft && initialDraftState) {
            const restoredDraft = applyBannerDraft({
                baseData: initialDraftState.data,
                draft: storedDraft,
            })

            setData(restoredDraft.data)
        }

        setDraftReady(true)
    }, [draftKey])

    useEffect(() => {
        if (!draftReady) {
            return
        }

        // 신규 배너 등록은 목록 query 대신 sessionStorage로 draft를 이어간다.
        const nextDraft = createBannerDraftPayload({
            data,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearBannerDraft(draftKey)
            return
        }

        saveBannerDraft(draftKey, nextDraft)
    }, [data, draftKey, draftReady])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const moveToList = (snackbarState) => {
        moveToBannerList({
            draftKey,
            clearDraft: clearBannerDraft,
            setNavigationUnlocked,
            history,
            listSearch,
            snackbarState,
            curPage: listCurPage,
            searchText: listSearchText,
            searchSelect: listSearchSelect,
        })
    }

    const handleBackClick = (event) => {
        handleBannerBackNavigation({
            event,
            leaveGuardEnabled,
            confirmLeave: () => window.confirm(BANNER_IMAGE_LEAVE_MESSAGE),
            moveToList,
        })
    }

    const handleUpdateField = (e) => {
        setData((prev) => applyBannerFieldChange({
            data: prev,
            name: e.target.name,
            value: e.target.value,
        }))
    }

    const handleUploadClick = (e, type) => {
        handleBannerImageUpload({
            event: e,
            imageType: type,
            setData,
            setMainImage,
            setMainImage2,
            onInvalidFile: (message = BANNER_IMAGE_FILE_ERROR_MESSAGE) => {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(message)
            },
        })
    }

    const createData = async (params) => {
        try {
            setSaving(true)

            const { data } = await API({
                method: 'post',
                url: '/api/v1/admin/banner/create',
                data: params,
                headers: { "Content-Type": "multipart/form-data" }
            })

            if (data.code === 1) {
                moveToList({ severity: 'success', message: '배너를 등록했습니다.' })
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('배너를 등록하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        if (saving) {
            return
        }

        const { nextData, errorMessage, formData } = prepareBannerSubmit({
            data,
            requireMainImage: true,
        })

        setData(nextData)

        if (errorMessage) {
            setOpenSnackbar(true)
            setSeveritySnackbar('warning')
            setMessageSnackbar(errorMessage)
            return
        }

        createData(formData)
    }

    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Title>배너 등록</Title>
                <Box component="form" encType="multipart/form-data" onSubmit={handleSubmit} sx={{ opacity: saving ? 0.6 : 1 }}>
                    <Prompt when={leaveGuardEnabled} message={BANNER_IMAGE_LEAVE_MESSAGE} />
                    <Grid container spacing={2} sx={{ marginTop: 2 }}>
                        <Grid item xs={12} md={7}>
                            <FormSection
                                title="배너 내용"
                                description="배너에 표시할 문구와 이동 URL을 작성합니다."
                            >
                                <FormControl fullWidth>
                                    <TextField
                                        label="제목"
                                        id="title"
                                        name="title"
                                        value={data.title}
                                        onChange={handleUpdateField}
                                        size="small"
                                        disabled={saving}
                                    />
                                </FormControl>
                                <FormControl fullWidth>
                                    <TextField
                                        label="부제목"
                                        id="titleSub"
                                        name="titleSub"
                                        value={data.titleSub}
                                        onChange={handleUpdateField}
                                        size="small"
                                        disabled={saving}
                                    />
                                </FormControl>
                                <FormControl fullWidth>
                                    <TextField
                                        label="URL"
                                        id="url"
                                        name="url"
                                        value={data.url}
                                        onChange={handleUpdateField}
                                        size="small"
                                        disabled={saving}
                                    />
                                </FormControl>
                            </FormSection>
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <FormSection
                                title="노출 설정"
                                description="노출 상태, 위치, 기간, 정렬 순서를 함께 설정합니다."
                            >
                                <SettingChoiceGroup
                                    title="상태"
                                    name="status"
                                    value={data.status}
                                    options={BANNER_STATUS_OPTIONS}
                                    disabled={saving}
                                    onChange={handleUpdateField}
                                />
                                <FormControl fullWidth>
                                    <TextField
                                        label="위치"
                                        select
                                        id="location"
                                        name="location"
                                        value={data.location}
                                        onChange={handleUpdateField}
                                        size="small"
                                        disabled={saving}
                                    >
                                        <MenuItem value='top'>top</MenuItem>
                                        <MenuItem value='middle'>middle</MenuItem>
                                        <MenuItem value='bottom'>bottom</MenuItem>
                                        <MenuItem value='popup'>popup</MenuItem>
                                    </TextField>
                                </FormControl>
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
                                    <FormControl fullWidth>
                                        <LocalizationProvider dateAdapter={AdapterDateFns} locale={koLocale}>
                                            <DesktopDatePicker
                                                required
                                                disabled={saving}
                                                inputFormat="yyyy-MM-dd"
                                                mask={"____-__-__"}
                                                label="StartTime"
                                                value={data.startTime}
                                                onChange={date => setData({
                                                    ...data,
                                                    startTime: date
                                                })}
                                                renderInput={(params) => <TextField required {...params} size="small"/>}
                                            />
                                        </LocalizationProvider>
                                    </FormControl>
                                    <FormControl fullWidth>
                                        <LocalizationProvider dateAdapter={AdapterDateFns} locale={koLocale}>
                                            <DesktopDatePicker
                                                disabled={saving}
                                                inputFormat="yyyy-MM-dd"
                                                mask={"____-__-__"}
                                                label="EndTime"
                                                value={data.endTime}
                                                onChange={date => setData({
                                                    ...data,
                                                    endTime: date
                                                })}
                                                renderInput={(params) => <TextField required {...params} size="small" />}
                                            />
                                        </LocalizationProvider>
                                    </FormControl>
                                </Box>
                                <Box>
                                    <FormControl sx={{ width: 120 }}>
                                        <TextField
                                            type="number"
                                            InputProps={{ inputProps: { min: 0, max: 99 } }}
                                            label="노출순서"
                                            id="order"
                                            name="order"
                                            value={data.order}
                                            onChange={handleUpdateField}
                                            size="small"
                                            disabled={saving}
                                        />
                                    </FormControl>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                        숫자가 작을수록 먼저 노출됩니다. 입력 범위는 0~99입니다.
                                    </Typography>
                                </Box>
                            </FormSection>
                        </Grid>
                        {imageFieldConfigs.map((fieldConfig) => (
                            <Grid key={fieldConfig.inputId} item xs={12} md={6}>
                                <BannerImageUploadPanel
                                    fieldConfig={fieldConfig}
                                    imageSrc={fieldConfig.fieldName === 'image' ? mainImage : mainImage2}
                                    imageFile={data[fieldConfig.fieldName]}
                                    disabled={saving}
                                    onUploadClick={handleUploadClick}
                                />
                            </Grid>
                        ))}
                    </Grid>
                    <BottomButtonGroup
                        visible={true}
                        submitDisabled={saving}
                        submitLabel={saving ? '저장 중...' : '저장'}
                        backDisabled={saving}
                        onBackClick={handleBackClick}
                    />
                </Box>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Paper>
        </Grid>
    )
}
